/**
 * Button variant / size constants.
 *
 * Import these instead of writing the string literal:
 *
 *     import Button, { BUTTON_VARIANT, BUTTON_SIZE } from "@/components/Common/Button";
 *     <Button variant={BUTTON_VARIANT.OUTLINE} size={BUTTON_SIZE.SM}>…</Button>
 *
 * ⚠️ WHY: a mistyped variant fails SILENTLY. `styles[variant]` for an unknown key
 * yields `undefined`, which drops out of the className join — so the button renders
 * with base styles only (no fill, no border) and nothing errors. Same failure class
 * as the landing pages' `REGION`/`SLUG` constants, and the same fix: a frozen
 * object turns a typo into an obvious `undefined` at the call site.
 *
 * Keys mirror the class names in `Button.module.scss`. Adding a variant means
 * adding it in BOTH places — the values are the contract between them.
 */
export const BUTTON_VARIANT = Object.freeze({
  /** Primary coral fill. */
  FILLED: "filled",
  /** Coral outline — the landing pages' default CTA treatment. */
  OUTLINE: "outline",
  /** Neutral-bordered, for use beside a filled button. */
  SECONDARY: "secondary",
  /** Navy fill, for a high-contrast CTA on a light band. */
  DARK: "dark",
  /** White fill, for use ON a dark or image background. */
  LIGHT: "light",
  /** Outlined white — ghost treatment on a dark band. */
  GHOST_LIGHT: "ghostLight",
  /** Chromeless text link that keeps button semantics and hit area. */
  TEXT: "text",
});

export const BUTTON_SIZE = Object.freeze({
  /** 34px — mobile CTA rows. */
  SM: "sm",
  /** 44px — the Figma desktop CTA. Default. */
  MD: "md",
  /** 52px. */
  LG: "lg",
});

/** All valid values, for the dev-only warning in the component. */
export const BUTTON_VARIANTS = Object.values(BUTTON_VARIANT);
export const BUTTON_SIZES = Object.values(BUTTON_SIZE);
